import AuthGuard from "components/authentication/AuthGuard";
import ExternalAuthGuard from "components/authentication/ExternalAuthGuard";
import GuestGuard from "components/authentication/GuestGuard";
import DashboardLayout from "components/Layouts/DashboardLayout";
import LoadingScreen from "components/LoadingScreen";
import ContactView from "pages/contact/ContactView";
import LandingPage from "pages/homePage/LandingPage";
import ScheduleView from "pages/schedule/ScheduleView";
import { FC, lazy, LazyExoticComponent, Suspense } from "react";
import { Navigate } from "react-router-dom";
import { APP_SCHEDULE_PLAN } from "utils/constants";

const Loadable =
  (Component: LazyExoticComponent<FC>) => (props: Record<string, never>) =>
    (
      <Suspense fallback={<LoadingScreen />}>
        <Component {...props} />
      </Suspense>
    );

// authentication pages
const Login = Loadable(lazy(() => import("./pages/authentication/Login")));
const ExternalLogin = Loadable(
  lazy(() => import("./pages/authentication/ExternalLogin"))
);
const Register = Loadable(
  lazy(() => import("./pages/authentication/Register"))
);
const ExternalRegister = Loadable(
  lazy(() => import("./pages/authentication/ExternalRegister"))
);
const ForgetPassword = Loadable(
  lazy(() => import("./pages/authentication/ForgetPassword"))
);

// Dashboard pages
const DashboardSaaS = Loadable(lazy(() => import("./pages/dashboards/SaaS")));
const CustomerDashboard = Loadable(
  lazy(() => import("./pages/customerAccess/CustomerDashboard"))
);
const CustomerSchedule = Loadable(
  lazy(() => import("./pages/customerAccess/CustomerSchedule"))
);
const CustomerReversations = Loadable(
  lazy(() => import("./pages/customerAccess/CustomerReversations"))
);
const SchedulePlanList = Loadable(
  lazy(() => import("./pages/schedulePlan/SchedulePlanList"))
);

// user profile
const UserProfile = Loadable(lazy(() => import("./pages/UserProfile")));

// user management
const ContactList = Loadable(lazy(() => import("./pages/contact/ContactList")));
const AddEditContact = Loadable(
  lazy(() => import("./pages/contact/AddEditContact"))
);
const UserGrid = Loadable(
  lazy(() => import("./pages/userManagement/UserGrid"))
);
const AddNewUser = Loadable(
  lazy(() => import("./pages/userManagement/AddNewUser"))
);

const AddEditSportCourt = Loadable(
  lazy(() => import("./pages/userManagement/sportCourt/AddEditSportCourt"))
);

const AddEditSportCourtTax = Loadable(
  lazy(
    () => import("./pages/userManagement/sportCourtTax/AddEditSportCourtTax")
  )
);

const AddEditCompany = Loadable(
  lazy(() => import("./pages/userManagement/company/AddEditCompany"))
);

// error
const Error = Loadable(lazy(() => import("./pages/404")));

const PaymentResult = Loadable(lazy(() => import("./pages/PaymentResult")));

// routes
const routes = [
  {
    path: "/",
    //element: <Navigate to="main" />,
    element: <LandingPage />,
  },
  {
    path: "external",
    element: <Navigate to="external" />,
  },
  {
    path: "login",
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    ),
  },
  {
    path: "customer-login/company/:companyURL", // http://localhost:3000/customer-login/company/empresa-ltda
    element: (
      <GuestGuard>
        <ExternalLogin />
      </GuestGuard>
    ),
  },
  {
    path: "register",
    element: (
      <GuestGuard>
        <Register />
      </GuestGuard>
    ),
  },
  {
    path: "customer-register/company/:companyURL", // http://localhost:3000/customer-register/company/empresa-ltda
    element: (
      <GuestGuard>
        <ExternalRegister />
      </GuestGuard>
    ),
  },
  {
    path: "forget-password",
    element: (
      <GuestGuard>
        <ForgetPassword />
      </GuestGuard>
    ),
  },
  {
    path: "main",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: <DashboardSaaS />,
      },
      {
        path: "administration",
        element: <UserProfile />,
      },
      {
        path: "administration/sport-court/add-edit/:id?",
        element: <AddEditSportCourt />,
      },
      {
        path: "administration/sport-court-tax/add-edit/:id?",
        element: <AddEditSportCourtTax />,
      },
      {
        path: "administration/company/edit",
        element: <AddEditCompany />,
      },
      {
        path: "contact",
        element: <ContactList />,
      },
      {
        path: "contact/add-edit/:id?",
        element: <AddEditContact />,
      },
      {
        path: "contact/view/:id",
        element: <ContactView />,
      },
      {
        path: "schedule",
        element: <ScheduleView />,
      },
      {
        path: APP_SCHEDULE_PLAN.ROUTE,
        element: <SchedulePlanList />,
      },
      {
        path: "user-grid",
        element: <UserGrid />,
      },
      {
        path: "add-user",
        element: <AddNewUser />,
      },
    ],
  },
  {
    path: "customer-access",
    element: (
      <ExternalAuthGuard>
        <DashboardLayout isCustomerAccess={true} />
      </ExternalAuthGuard>
    ),
    children: [
      {
        path: "",
        element: <CustomerDashboard />,
      },
      {
        path: "schedule",
        element: <CustomerSchedule />,
      },
      {
        path: "reservations",
        element: <CustomerReversations />,
      },
    ],
  },
  {
    path: "payment-result",
    element: <PaymentResult />,
  },
  {
    path: "*",
    element: <Error />,
  },
];

export default routes;
